import type { AnimationEvent } from '@angular/animations';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, input } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';

import { asyncScheduler, filter, observeOn, Subject } from 'rxjs';

export type TooltipPosition =
  | 'left'
  | 'right'
  | 'above'
  | 'below'
  | 'before'
  | 'after'
  | 'aboveright'
  | 'righttop'
  | 'leftbelow'
  | 'rightbelow';
export type TooltipVisibility = 'initial' | 'visible' | 'hidden';
export type TooltipType =
  | 'default'
  | 'light'
  | 'template-only'
  | 'kiwi'
  | 'javelo'
  | 'recruitee'
  | 'tellent';

const scaleAnimation = [
  state('void', style({ transform: 'scale(0)' })),
  state('initial', style({ transform: 'scale(0)' })),
  state('visible', style({ transform: 'scale(1)' })),
  state('hidden', style({ transform: 'scale(0)' })),
];

const cubicBezierTransition = [
  transition('* => visible', animate('150ms cubic-bezier(0.0, 0.0, 0.2, 1)')),
  transition('* => hidden', animate('150ms cubic-bezier(0.4, 0.0, 1, 1)')),
];

@Component({
  selector: 'rt-tooltip-inner',
  templateUrl: './tooltip.html',
  styleUrls: ['./tooltip.less'],
  animations: [
    trigger('scale', [...scaleAnimation, ...cubicBezierTransition]),
    trigger('scaleNoTransition', scaleAnimation),
  ],
  host: {
    '[@scale]': '!skipTransition() && visibility()',
    '[@scaleNoTransition]': 'skipTransition() && visibility()',
    '[style.transform-origin]': 'transformOrigin()',
    '[class]': 'position()',
    '[class.light]': `type() === 'light'`,
    '[class.kiwihr]': `type() === 'kiwi'`,
    '[class.javelo]': `type() === 'javelo'`,
    '[class.recruitee]': `type() === 'recruitee'`,
    '[class.tellent]': `type() === 'tellent'`,
    '[class.template-only]': `type() === 'template-only'`,
    '[class.has-border]': 'hasBorder()',
    '[class.has-shadow]': 'hasShadow()',
    '[class.has-no-arrow]': 'hasNoArrow()',
    '[class.has-text-align-center]': 'hasTextAlignCenter()',
    '[style.--tooltip-arrow-offset-x]': '(arrowOffset()?.offsetX || 0) + "px"',
    '[style.--tooltip-arrow-offset-y]': '(arrowOffset()?.offsetY || 0) + "px"',
    '(@scale.done)': 'handleAfterVisibilityAnimation($event)',
    '(@scaleNoTransition.done)': 'handleAfterVisibilityAnimation($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class TooltipInnerComponent {
  public readonly message = input<string>(undefined!);
  public readonly type = input<TooltipType>('default');
  public readonly hasBorder = input<boolean>(undefined!);
  public readonly hasShadow = input<boolean>(undefined!);
  public readonly hasNoArrow = input<boolean>(undefined!);
  public readonly hasTextAlignCenter = input<boolean>(undefined!);
  public readonly visibility = input<TooltipVisibility>('initial');
  public readonly position = input<TooltipPosition>(undefined!);
  public readonly transformOrigin = input<string>('above');
  public readonly skipTransition = input<boolean>(false);
  public readonly arrowOffset = input<{
    offsetX: number;
    offsetY: number;
  }>();
  @Input() public templateRef: any;

  #animationState = new Subject<AnimationEvent>();

  public animationState = outputFromObservable<AnimationEvent>(
    this.#animationState.pipe(observeOn(asyncScheduler)),
  );
  public hide = outputFromObservable(
    this.#animationState.pipe(
      observeOn(asyncScheduler),
      filter(event => event.toState === 'hidden' && !this.isVisible()),
    ),
  );

  public isVisible(): boolean {
    return this.visibility() === 'visible';
  }

  public handleAfterVisibilityAnimation(event: AnimationEvent): void {
    this.#animationState.next(event);
  }
}
